import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { newsApi, type ArticleSummary } from "../../api/news";
import type { ApiError } from "../../api/client";

export default function NewsListPage() {
  const { i18n, t } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";

  const [items, setItems] = useState<ArticleSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void newsApi
      .list({ locale, page: 0, size: 50 })
      .then((p) => setItems(p.content.filter(Boolean)))
      .catch((e: ApiError) => setError(e.message));
  }, [locale]);

  const [hero, ...rest] = items ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <header>
        <h1 style={{ margin: 0, fontSize: 28, color: "var(--ink)" }}>
          {t("news.title", "News")}
        </h1>
        <p style={{ margin: "4px 0 0", color: "var(--ink-3)", fontSize: 14 }}>
          {t("news.subtitle", "Novosti iz Bankoma i partnerske mreže.")}
        </p>
      </header>

      {error && <div style={errorBox}>{error}</div>}

      {!items ? (
        <p style={{ color: "var(--ink-3)" }}>{t("common.loading", "Loading…")}</p>
      ) : items.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("news.empty", "No news yet.")}</p>
      ) : (
        <>
          {hero && <HeroCard a={hero} locale={locale} />}
          {rest.length > 0 && (
            <div style={grid}>
              {rest.map((a) => (
                <NewsCard key={a.id} a={a} locale={locale} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function HeroCard({ a, locale }: { a: ArticleSummary; locale: string }) {
  return (
    <Link to={`/news/${a.slug}`} style={heroCard} className="news-hero-tile">
      <Cover src={a.coverMinioKey} title={a.title} large />
      <div style={heroBody}>
        <Chip slug={a.categorySlug} />
        <h2 style={{ margin: 0, fontSize: 22, color: "var(--ink)", lineHeight: 1.3 }}>{a.title}</h2>
        {a.excerpt && (
          <p style={{ margin: 0, color: "var(--ink-2)", fontSize: 14, lineHeight: 1.6 }}>{a.excerpt}</p>
        )}
        {a.publishedAt && (
          <small style={meta}>{new Date(a.publishedAt).toLocaleDateString(locale, dateFmt)}</small>
        )}
      </div>
    </Link>
  );
}

function NewsCard({ a, locale }: { a: ArticleSummary; locale: string }) {
  return (
    <Link to={`/news/${a.slug}`} style={card} className="news-tile">
      <Cover src={a.coverMinioKey} title={a.title} />
      <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: 14 }}>
        <Chip slug={a.categorySlug} />
        <strong style={{ fontSize: 15, color: "var(--ink)", lineHeight: 1.35 }}>{a.title}</strong>
        {a.excerpt && (
          <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 13, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{a.excerpt}</p>
        )}
        {a.publishedAt && (
          <small style={meta}>{new Date(a.publishedAt).toLocaleDateString(locale, dateFmt)}</small>
        )}
      </div>
    </Link>
  );
}

function Cover({ src, title, large }: { src: string | null; title: string; large?: boolean }) {
  const isUrl = src != null && /^https?:\/\//i.test(src);
  if (isUrl) {
    return (
      <div style={{ ...coverWrap, aspectRatio: large ? "21 / 9" : "16 / 9" }}>
        <img src={src!} alt="" style={coverImg} loading="lazy" />
      </div>
    );
  }
  // Fallback — initials over a brand gradient. Less "debug-looking" than No cover.
  const initials = title
    .replace(/[^\p{L}\s]/gu, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
  return (
    <div
      style={{
        ...coverWrap,
        aspectRatio: large ? "21 / 9" : "16 / 9",
        background: "linear-gradient(135deg, var(--brand-primary-600), var(--brand-primary))",
        color: "color-mix(in srgb, var(--on-primary) 90%, transparent)",
        display: "grid",
        placeItems: "center",
        fontWeight: 700,
        fontSize: large ? 56 : 32,
        letterSpacing: 1,
      }}
    >
      {initials || "BK"}
    </div>
  );
}

function Chip({ slug }: { slug: string }) {
  // "news-wp" is the auto-imported bucket — display it as just "News" to the user.
  const label = slug === "news-wp" ? "News" : slug;
  return <span style={chip}>{label}</span>;
}

const dateFmt: Intl.DateTimeFormatOptions = { day: "numeric", month: "long", year: "numeric" };

const grid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
  gap: 20,
};
const card: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  background: "var(--surface)",
  borderRadius: 12,
  boxShadow: "var(--shadow-1)",
  border: "1px solid var(--border)",
  textDecoration: "none",
  overflow: "hidden",
  transition: "transform .15s ease, box-shadow .15s ease",
};
const heroCard: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1.4fr 1fr",
  gap: 0,
  background: "var(--surface)",
  borderRadius: 14,
  boxShadow: "var(--shadow-2)",
  border: "1px solid var(--border)",
  textDecoration: "none",
  overflow: "hidden",
  transition: "transform .15s ease, box-shadow .15s ease",
};
const heroBody: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  padding: "28px 28px 24px",
  justifyContent: "center",
};
const coverWrap: React.CSSProperties = {
  width: "100%",
  background: "var(--surface-2)",
  overflow: "hidden",
};
const coverImg: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
};
const chip: React.CSSProperties = {
  alignSelf: "flex-start",
  background: "var(--brand-primary-50)",
  color: "var(--brand-ink)",
  fontSize: 11,
  padding: "3px 10px",
  borderRadius: 999,
  fontWeight: 600,
  letterSpacing: 0.3,
  textTransform: "uppercase",
};
const meta: React.CSSProperties = { color: "var(--ink-4)", fontSize: 12, marginTop: 4 };
const errorBox: React.CSSProperties = { padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
